import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, X, Loader2, ArrowLeft, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

// ─── Debounce Hook ───
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// ─── Highlight matching text ───
const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  try {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return <>{parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <span key={i} className="text-blue-600 font-semibold">{part}</span>
        : <span key={i}>{part}</span>
    )}</>;
  } catch { return <>{text}</>; }
};

// ─── In-memory query cache (survives re-renders, clears on page unload) ───
const queryCache = new Map();

// ─── Default bias center (Hyderabad) ───
const DEFAULT_BIAS = { lat: 17.385, lng: 78.4867 };

// ─── Google Maps loader utility ───
const getGoogleMaps = () => {
  if (window.google?.maps?.places) return window.google.maps;
  return null;
};

/**
 * Shared Google Places Autocomplete Component
 *
 * Props:
 *  onSelect(location)  — called with { place_id, location_text, formatted_address, latitude, longitude, city, state, postal_code }
 *  onClose()           — close callback (fullscreen mode)
 *  mode                — 'inline' | 'fullscreen'
 *  placeholder         — input placeholder
 *  initialValue        — pre-fill text
 *  className           — wrapper class
 *  showCurrentLocation — show "Use my current location" row (default true)
 *  biasCenter          — { lat, lng } override for location bias
 */
export const GooglePlacesAutocomplete = ({
  onSelect,
  onClose,
  mode = 'inline',
  placeholder = 'Search location...',
  initialValue = '',
  className = '',
  showCurrentLocation = true,
  biasCenter,
}) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [apiError, setApiError] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placesRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);
  const containerRef = useRef(null);

  const debouncedQuery = useDebounce(query, 450);

  // ─── Init Google Places ───
  useEffect(() => {
    let attempts = 0;
    const tryInit = () => {
      // Detect auth/referrer failure surfaced by index.html's gm_authFailure handler
      if (window.__GM_AUTH_FAILED__) {
        setApiError('Location search blocked. Google Maps key is not authorized for this domain.');
        return true;
      }
      const gm = getGoogleMaps();
      if (gm) {
        autocompleteRef.current = new gm.places.AutocompleteService();
        sessionTokenRef.current = new gm.places.AutocompleteSessionToken();
        const div = document.createElement('div');
        div.style.display = 'none';
        document.body.appendChild(div);
        const map = new gm.Map(div, { center: DEFAULT_BIAS, zoom: 10 });
        placesRef.current = new gm.places.PlacesService(map);
        return true;
      }
      return false;
    };
    if (!tryInit()) {
      const id = setInterval(() => {
        attempts++;
        if (tryInit() || attempts >= 30) {
          clearInterval(id);
          if (attempts >= 30 && !getGoogleMaps()) {
            setApiError('Location search unavailable. Check Google Maps API key/billing.');
          }
        }
      }, 500);
      return () => clearInterval(id);
    }
    if (mode === 'fullscreen') setTimeout(() => inputRef.current?.focus(), 150);
  }, [mode]);

  // ─── Search on debounced query ───
  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      searchPlaces(debouncedQuery);
    } else {
      setResults([]);
      setShowResults(false);
      setHasSearched(false);
    }
  }, [debouncedQuery]);

  // ─── Click outside (inline) ───
  useEffect(() => {
    if (mode !== 'inline') return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mode]);

  // ─── Core search ───
  const searchPlaces = async (q) => {
    if (!autocompleteRef.current) return;

    // Cache check
    if (queryCache.has(q)) {
      setResults(queryCache.get(q));
      setShowResults(true);
      setHasSearched(true);
      setLoading(false);
      return;
    }

    const thisRequestId = ++requestIdRef.current;
    setLoading(true);
    setHasSearched(false);

    const gm = getGoogleMaps();
    const center = biasCenter || DEFAULT_BIAS;
    const biasCircle = new gm.Circle({ center, radius: 50000 });

    autocompleteRef.current.getPlacePredictions(
      {
        input: q,
        componentRestrictions: { country: 'in' },
        language: 'en',
        locationBias: biasCircle.getBounds(),
        sessionToken: sessionTokenRef.current,
      },
      (predictions, status) => {
        // Stale response guard
        if (thisRequestId !== requestIdRef.current) return;

        // Detect auth/referrer failure that may have happened during the request
        if (window.__GM_AUTH_FAILED__) {
          setApiError('Location search blocked. Google Maps key is not authorized for this domain.');
          setResults([]);
          setLoading(false);
          setHasSearched(true);
          return;
        }

        if (status === gm.places.PlacesServiceStatus.OK && predictions) {
          const mapped = predictions.slice(0, 5).map(p => ({
            place_id: p.place_id,
            mainText: p.structured_formatting?.main_text || p.description.split(',')[0],
            secondaryText: p.structured_formatting?.secondary_text || p.description.split(',').slice(1).join(','),
            fullDescription: p.description,
          }));
          queryCache.set(q, mapped);
          setResults(mapped);
          setShowResults(true);
        } else {
          setResults([]);
          if (status === gm.places.PlacesServiceStatus.REQUEST_DENIED) {
            setApiError('Google Maps API key issue. Contact support.');
          } else if (status === gm.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
            setApiError('Search limit reached. Try again in a minute.');
          }
        }
        setLoading(false);
        setHasSearched(true);
      }
    );
  };

  // ─── Select a place ───
  const handleSelectPlace = useCallback((result) => {
    if (!placesRef.current) { toast.error('Google Places not ready'); return; }
    setLoading(true);
    placesRef.current.getDetails(
      {
        placeId: result.place_id,
        fields: ['geometry', 'formatted_address', 'address_components', 'name', 'place_id'],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        // New session token after selection
        const gm = getGoogleMaps();
        if (gm) sessionTokenRef.current = new gm.places.AutocompleteSessionToken();

        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          let city = '', state = '', postal_code = '';
          place.address_components?.forEach(c => {
            if (c.types.includes('locality')) city = c.long_name;
            if (c.types.includes('administrative_area_level_1')) state = c.long_name;
            if (c.types.includes('postal_code')) postal_code = c.long_name;
          });
          onSelect({
            place_id: result.place_id,
            location_text: result.mainText,
            formatted_address: place.formatted_address,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            city, state, postal_code,
          });
          setQuery(result.mainText);
          setShowResults(false);
          setResults([]);
        } else {
          toast.error('Could not get location details');
        }
        setLoading(false);
      }
    );
  }, [onSelect]);

  // ─── Use current location ───
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        // Reverse geocode
        const gm = getGoogleMaps();
        if (gm) {
          const geocoder = new gm.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            setGettingLocation(false);
            if (status === 'OK' && results[0]) {
              let city = '', state = '', postal_code = '';
              results[0].address_components?.forEach(c => {
                if (c.types.includes('locality')) city = c.long_name;
                if (c.types.includes('administrative_area_level_1')) state = c.long_name;
                if (c.types.includes('postal_code')) postal_code = c.long_name;
              });
              const locationText = results[0].address_components?.find(c => c.types.includes('sublocality'))?.long_name || city || 'Current Location';
              onSelect({
                place_id: results[0].place_id || '',
                location_text: locationText,
                formatted_address: results[0].formatted_address,
                latitude, longitude, city, state, postal_code,
              });
              setQuery(locationText);
              setShowResults(false);
            } else {
              onSelect({ place_id: '', location_text: 'Current Location', formatted_address: '', latitude, longitude, city: '', state: '', postal_code: '' });
            }
          });
        } else {
          setGettingLocation(false);
          onSelect({ place_id: '', location_text: 'Current Location', formatted_address: '', latitude, longitude, city: '', state: '', postal_code: '' });
        }
      },
      (err) => {
        setGettingLocation(false);
        toast.error(err.code === 1 ? 'Location permission denied' : 'Could not get location');
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleClear = () => { setQuery(''); setResults([]); setShowResults(false); setHasSearched(false); inputRef.current?.focus(); };

  const popularAreas = ['Hyderabad', 'Vijayawada', 'Guntur', 'Tirupati', 'Warangal', 'Bangalore', 'Chennai'];

  // ─── Shared Result Row ───
  const ResultRow = ({ result, idx }) => (
    <button
      key={result.place_id}
      onClick={() => handleSelectPlace(result)}
      className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
      data-testid={`location-result-${idx}`}
    >
      <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
        <MapPin className="w-4 h-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-medium text-gray-900 leading-tight"><HighlightText text={result.mainText} query={query} /></p>
        <p className="text-[13px] text-[#70757a] mt-0.5 truncate leading-tight">{result.secondaryText}</p>
      </div>
    </button>
  );

  // ─── Shared Results Body ───
  const ResultsBody = () => {
    if (apiError) return <div className="py-6 px-4 text-center"><p className="text-sm text-red-500">{apiError}</p></div>;
    if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-blue-500 animate-spin" /></div>;
    if (results.length > 0) return <div className="py-1">{results.map((r, i) => <ResultRow key={r.place_id} result={r} idx={i} />)}</div>;
    if (hasSearched && query.length >= 3) return (
      <div className="py-8 text-center">
        <MapPin className="w-9 h-9 text-gray-200 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No results for "{query}"</p>
        <p className="text-xs text-gray-400 mt-1">Try different spelling</p>
      </div>
    );
    // Default: current location + popular areas
    return (
      <div className="py-2">
        {showCurrentLocation && (
          <button onClick={handleUseCurrentLocation} disabled={gettingLocation}
            className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-blue-50 active:bg-blue-100 transition-colors border-b border-gray-100"
            data-testid="use-current-location"
          >
            <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
              {gettingLocation ? <Loader2 className="w-4 h-4 text-blue-500 animate-spin" /> : <Crosshair className="w-4 h-4 text-blue-500" />}
            </div>
            <p className="text-[15px] font-medium text-blue-600">Use my current location</p>
          </button>
        )}
        <div className="px-4 pt-3 pb-2">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Popular Areas</p>
          <div className="flex flex-wrap gap-2">
            {popularAreas.map(a => (
              <button key={a} onClick={() => setQuery(a)}
                className="px-3.5 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              >{a}</button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ─── FULLSCREEN MODE ───
  if (mode === 'fullscreen') {
    return (
      <div className="fixed inset-0 bg-white z-[2000] flex flex-col">
        <div className="flex items-center gap-2 px-3 py-2.5 bg-white border-b border-gray-100 shadow-sm">
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors" data-testid="close-location-search">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1 flex items-center bg-gray-100 rounded-full px-4 py-2.5">
            <Search className="w-4 h-4 text-gray-400 mr-2.5 flex-shrink-0" />
            <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder={placeholder} className="flex-1 bg-transparent border-none outline-none text-[15px] text-gray-900 placeholder-gray-400"
              autoComplete="off" autoCorrect="off" spellCheck="false" data-testid="location-search-input"
            />
            {loading && <Loader2 className="w-4 h-4 text-blue-500 animate-spin ml-1" />}
            {query && !loading && (
              <button onClick={handleClear} className="ml-1 p-1 rounded-full hover:bg-gray-200"><X className="w-4 h-4 text-gray-500" /></button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-white"><ResultsBody /></div>
      </div>
    );
  }

  // ─── INLINE MODE ───
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center bg-gray-100 rounded-xl px-3.5 py-2.5">
        <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
        <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setShowResults(true); else if (!query) setShowResults(true); }}
          placeholder={placeholder} className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400"
          autoComplete="off" data-testid="location-search-input"
        />
        {loading && <Loader2 className="w-4 h-4 text-blue-500 animate-spin ml-1" />}
        {query && !loading && <button onClick={handleClear} className="ml-1 p-0.5"><X className="w-4 h-4 text-gray-400" /></button>}
      </div>
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-[320px] overflow-y-auto">
          <ResultsBody />
        </div>
      )}
    </div>
  );
};

// ─── HOOK VERSION (for MapSearch, Requirements custom UIs) ───
export const useGooglePlacesAutocomplete = () => {
  const autocompleteRef = useRef(null);
  const placesRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let attempts = 0;
    const tryInit = () => {
      const gm = getGoogleMaps();
      if (gm) {
        autocompleteRef.current = new gm.places.AutocompleteService();
        sessionTokenRef.current = new gm.places.AutocompleteSessionToken();
        const div = document.createElement('div');
        div.style.display = 'none';
        document.body.appendChild(div);
        const map = new gm.Map(div, { center: DEFAULT_BIAS, zoom: 10 });
        placesRef.current = new gm.places.PlacesService(map);
        setIsReady(true);
        return true;
      }
      return false;
    };
    if (!tryInit()) {
      const id = setInterval(() => {
        attempts++;
        if (tryInit() || attempts >= 30) clearInterval(id);
      }, 500);
      return () => clearInterval(id);
    }
  }, []);

  const search = useCallback((q) => new Promise((resolve) => {
    if (!autocompleteRef.current || !q || q.length < 3) { resolve([]); return; }
    if (queryCache.has(q)) { resolve(queryCache.get(q)); return; }

    const thisId = ++requestIdRef.current;
    const gm = getGoogleMaps();
    const circle = new gm.Circle({ center: DEFAULT_BIAS, radius: 50000 });

    autocompleteRef.current.getPlacePredictions(
      { input: q, componentRestrictions: { country: 'in' }, language: 'en', locationBias: circle.getBounds(), sessionToken: sessionTokenRef.current },
      (predictions, status) => {
        if (thisId !== requestIdRef.current) { resolve([]); return; }
        if (status === gm.places.PlacesServiceStatus.OK && predictions) {
          const mapped = predictions.slice(0, 5).map(p => ({
            place_id: p.place_id,
            mainText: p.structured_formatting?.main_text || p.description.split(',')[0],
            secondaryText: p.structured_formatting?.secondary_text || p.description.split(',').slice(1).join(','),
            fullDescription: p.description,
          }));
          queryCache.set(q, mapped);
          resolve(mapped);
        } else { resolve([]); }
      }
    );
  }), []);

  const getPlaceDetails = useCallback((placeId) => new Promise((resolve, reject) => {
    if (!placesRef.current) { reject(new Error('Not initialized')); return; }
    placesRef.current.getDetails(
      { placeId, fields: ['geometry', 'formatted_address', 'address_components', 'name'], sessionToken: sessionTokenRef.current },
      (place, status) => {
        const gm = getGoogleMaps();
        if (gm) sessionTokenRef.current = new gm.places.AutocompleteSessionToken();
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          let city = '', state = '', postal_code = '';
          place.address_components?.forEach(c => {
            if (c.types.includes('locality')) city = c.long_name;
            if (c.types.includes('administrative_area_level_1')) state = c.long_name;
            if (c.types.includes('postal_code')) postal_code = c.long_name;
          });
          resolve({ latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng(), formatted_address: place.formatted_address, city, state, postal_code });
        } else { reject(new Error('Place details failed')); }
      }
    );
  }), []);

  return { search, searchPlaces: search, getPlaceDetails, isReady };
};

export default GooglePlacesAutocomplete;
